import { Action, ActionsDropdown, BarChart, Card, CardBody, CardHeader, Map, Modal, PieChart } from '@aidkitorg/component-library';
import { createStagedComponent, DashboardComponentWithId, StagedComponent, SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { RawGeoJSONSource } from '@aidkitorg/types/lib/survey';
import { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useDashboards } from '../../../../Contexts/DashboardsContext';
import { useAsyncEffect } from '../../../../Hooks/AsyncEffect';
import { Metric } from './Metric';
import { ComponentEditorModal } from './ComponentEditorModal';
import { useLocalizedStrings } from '../../../../Localization';

/**
 * Base dashboard component that handles all common behaviors of dashboard
 * components and decides which type of component to render.
 */
type DashboardComponentCardProps = {
  id: string
  component: SupportedDashboardComponent,
  isEditMode?: boolean,
  width?: number,
  height?: number,
}

export const DashboardComponentCard = ({ id, component, isEditMode, width, height }: DashboardComponentCardProps) => {
  const L = useLocalizedStrings()
  const [dashboardComponent, setDashboardComponent] = useState<SupportedDashboardComponent>(component)
  const { setStagedComponents, getDashboardComponentTranslation } = useDashboards()
  const [queryResult, setQueryResult] = useState<TranslatedComponentResult | undefined>()

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [chartHeight, setChartHeight] = useState(300)

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [dashboardComponent.title, dashboardComponent.description, width, height]);

  useEffect(() => {
    setChartHeight(height ? Math.max(height - headerHeight, 120) : 300)
  }, [height, headerHeight])
  
  // This shouldn't happen but in case we render this for a component that isn't a supported kind
  if (!('kind' in component) || !['Hero Number', 'Bar Chart', 'Pie Chart', 'Map'].includes(component.kind)) {
    return (<></>)
  }

  useEffect(() => {
    setDashboardComponent(component);
  }, [component]);

    
  useAsyncEffect(async () => {
    const result = await getDashboardComponentTranslation(dashboardComponent)
    if (result) {
      setQueryResult(result)
    } else {
      console.error('Failed to translate dashboard component:', dashboardComponent)
    }
  }, [dashboardComponent])

  const ComponentToRender = useMemo(() => {
    switch (dashboardComponent.kind) {
      case 'Hero Number':
        return <Metric component={dashboardComponent} result={queryResult && {
          summaryKey: queryResult.summaryKey,
          data: queryResult.data,
          timing: queryResult.timing,
          rowCount: queryResult.rowCount || 0
        }}/>
      case 'Bar Chart':
        // This should be a field type for admin panel dashboards
        const groupKey = dashboardComponent.groups[0].kind === 'Field' ? dashboardComponent.groups[0].field : ''
        const summaryKey = queryResult?.summaryKey || ''
        return (
          queryResult ? 
            <BarChart 
              data={queryResult.data}
              categoryKey={groupKey}
              valueKey={summaryKey}
              xAxisLabel={dashboardComponent.xAxisLabel}
              yAxisLabel={dashboardComponent.yAxisLabel}
              height={chartHeight}
            /> : 
            <></>
        )
      case 'Pie Chart':
        const nameKey = dashboardComponent.groups[0].kind === 'Field' ? dashboardComponent.groups[0].field : ''
        if (queryResult) {
          const dataKey = queryResult.summaryKey
          let processedData = Array.isArray(queryResult.data) ? queryResult.data.map((datum: Record<string, any>, _idx: number) => ({
            ...datum,
            [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
          })) : []

          if (processedData.length > 5) {
            // Sort by data value descending to get largest segments
            processedData.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0))
            
            // Keep top 4 segments
            const topFour = processedData.slice(0, 4)
            const remaining = processedData.slice(4)
            
            // Sum up remaining segments
            const otherValue = remaining.reduce((sum, item) => sum + (item[dataKey] || 0), 0)
            
            // Create "Other" segment
            const otherSegment = {
              [nameKey]: L.admin_panel.dashboards.other,
              [dataKey]: otherValue
            }
            
            // Combine top 4 with "Other"
            const summarizedData = [...topFour, otherSegment]
            return <PieChart 
              height={chartHeight}
              data={summarizedData}
              dataKey={dataKey}
              nameKey={nameKey}
            /> 
          }

          return <PieChart 
            height={chartHeight}
            data={processedData}
            dataKey={dataKey}
            nameKey={nameKey}
          /> 
        } else {
          return <></>
        }
      case 'Map':
        // Map components use a different data format (resultsMap)
        if (queryResult && queryResult.data) {
          // Transform the map sources with the GeoJSON data
          const sourcesWithData = dashboardComponent.sources.map((source, index) => {
            if (source.kind !== 'Applicant Data Source') return source

            const layerData = queryResult.data[source.layerName || `layer_${index}`]
            if (!layerData) return source

            return {
              ...source,
              kind: 'Raw GeoJSON Source',
              data: JSON.stringify(layerData.rows),
              // Clear fields that don't apply to Raw GeoJSON sources
              columns: undefined,
              filter: undefined,
              addressField: undefined,
              includeUrl: undefined,
              includeUid: undefined
            } as RawGeoJSONSource
          })

          return (
            <Map
              height={chartHeight}
              mapConfig={{
                ...dashboardComponent,
                sources: sourcesWithData,
                // Clear description field to match formatting
                description: undefined,
              }}
            />
          )
        } else {
          return <></>
        }
      default:
        return <></>
    }
  }, [dashboardComponent, queryResult, chartHeight]) // Only re-create when actual data changes

  const titleAndDesc = (
    <div className="drag-handle cursor-move" ref={headerRef}>
      <div className="text-sm break-normal px-4 py-2">
        {'title' in dashboardComponent && dashboardComponent.title ? 
          (typeof dashboardComponent.title === 'string' ? dashboardComponent.title : dashboardComponent.title['en']) :
          'Dashboard Component'
        }
      </div>
      {dashboardComponent.description && (
        <div className="text-xs break-normal text-gray-700 pb-2 px-4">
          {dashboardComponent.description}
        </div>
      )}
    </div>
  );

  const handleDeleteComponent = () => {
    setStagedComponents((prev) => [...prev].filter((sc) => sc.id !== id))
  }

  const componentActions: Array<Action<SupportedDashboardComponent>> = [
    {
      label: L.admin_panel.dashboards.edit,
      onClick: () => {setIsEditModalOpen(true)},
      icon: "PencilIcon",
    },
    {
      label: L.admin_panel.dashboards.duplicate,
      onClick: async () => {
        await duplicateComponent()
      },
      icon: "Square2StackIcon"
    },
    {
      label: L.admin_panel.dashboards.delete,
      onClick: () => {
        handleDeleteComponent()
      },
      icon: "TrashIcon",
      variant: "warn",

    }
  ]

  const duplicateComponent = async () => {
    setStagedComponents(prev => {
      const index = prev.findIndex(sc => sc.id === id)
      if (index === -1) return prev

      const original = prev[index]
      const duplicatedComponent = {
        ...original.config,
        title: original.config.title + ' (Copy)',
      };

      (async () => {
        const translations = await getDashboardComponentTranslation(duplicatedComponent)
        if (translations) {
          const stagedComponent = createStagedComponent(duplicatedComponent, translations)

          setStagedComponents(current => {
            const insertIndex = current.findIndex(sc => sc.id === id)
            if (insertIndex === -1) return current

            return [
              ...current.slice(0, insertIndex + 1),
              stagedComponent,
              ...current.slice(insertIndex + 1),
            ]
          })
        }
      })()

      return prev
    })
  }

  const cardButton = <ActionsDropdown 
    actions={componentActions}
    item={dashboardComponent}
  />

  return <Card className="rounded-md h-full">
    <CardHeader
      title={titleAndDesc}
      actionButton={isEditMode ? cardButton : undefined}
    />
    <CardBody className="flex-1 flex">
      <div className="flex-1 flex items-center justify-center">
        {ComponentToRender}
        <ComponentEditorModal
          id={id}
          component={dashboardComponent}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        />
      </div>
    </CardBody>
  </Card>
}